const VISIBLE_CLASS = 'is-visible';

/**
 * Reveals the footer's scroll-to-top button once the visitor has scrolled the configured share
 * of one screen, and returns them to the top when it is activated.
 */
function handleScrollToTop(): void {
  const button = document.querySelector<HTMLButtonElement>('[data-scroll-to-top]');
  if (!button) {
    return;
  }

  // The editor picks how far down a screen to wait, so the threshold scales with the device
  // rather than being a fixed pixel value that is too eager on phones and too reluctant on tall
  // desktops. Falls back to 50% if the attribute is ever missing or unparseable.
  const percent = Number(button.dataset.scrollToTop) || 50;

  let visible = false;
  let frameId = 0;

  const update = (): void => {
    frameId = 0;

    // Both values are read every pass so a resize, an orientation change or late-loading content
    // that changes the page height is accounted for without recomputing anything up front.
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

    // A page that fits the viewport has nothing to scroll back from, so the button stays hidden
    // however far down the threshold sits.
    const shouldShow = maxScroll > 0 && window.scrollY >= window.innerHeight * (percent / 100);

    // Scroll fires far more often than the state actually changes, so skip redundant writes.
    if (shouldShow !== visible) {
      visible = shouldShow;
      button.classList.toggle(VISIBLE_CLASS, shouldShow);
    }
  };

  // Batched into a frame so a burst of scroll events costs at most one class write per paint.
  const schedule = (): void => {
    if (!frameId) {
      frameId = window.requestAnimationFrame(update);
    }
  };

  button.addEventListener('click', () => {
    // The site sets scroll-behavior: smooth globally, so pass the behaviour explicitly to
    // override it for visitors who have asked for reduced motion.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  });

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });

  // Page height can change after load (lazy images, embeds, an accordion opening), which flips
  // whether the page is scrollable at all, and neither scroll nor resize fires for that.
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(schedule).observe(document.documentElement);
  }

  update();
}

document.addEventListener('DOMContentLoaded', handleScrollToTop);
